import { useCallback, useMemo, type FC } from 'react'
import styled from 'styled-components'
import { useDeviceType, useModal, useTranslator } from '@/hooks'
import { designGrid, mq, presets, rgbAlpha } from '@/styles'
import { MODALS } from '@/context'
import { TypeCmsHomePage } from '@/schema'
import { SliderFlickity } from './base'
import { ConditionalWrapper } from './base/ConditionalWrapper'

type Props = {
  data: TypeCmsHomePage['packs']
}

const getItems = (data: Props['data'], onClickCta, contactUs) => data.map((item, idx) => (
  <li key={idx} className='slide'>
    <article>
      <h3>{item.name}</h3>
      {item?.body && <div className='md' dangerouslySetInnerHTML={{ __html: item.body }} />}
      {item.price && <p className='price-wrapper'>
        <span className='discounted'>{item.discountPrice}</span>
        {item?.price && <span className='full'>{item.price}</span>}
        {item.promo && <span className='launch'>{item.promo}</span>}
      </p>}
      <button onClick={onClickCta}>{item.buttonText ?? contactUs}</button>
    </article>
  </li>
))

export const SectionPricing:FC<Props> = ({ data }) => {
  const { t } = useTranslator()
  const { toggle:toggleContact } = useModal(MODALS.CONTACT)
  const { isMobile } = useDeviceType()

  const onClickCta = useCallback(() => {
    toggleContact()
  }, [toggleContact])

  const headingId = useMemo(() => t('project.internal_page_links')?.[2]?.id?.substring(1) as string, [t])

  const Items = useMemo(() => getItems(data, onClickCta, t('contact_us') as string), [data, onClickCta, t])
  // const Items = useMemo(() => getItems(t('project.section_pricing.plans') as any[], onClickCta), [t, onClickCta])

  return (
    <Section aria-labelledby={headingId}>
      <h2 id={headingId}>{t('project.section_pricing.title')}:</h2>
      <ConditionalWrapper wrapperTrue={(children) => <SliderFlickity options={{ autoPlay: false, wrapAround: false, pageDots: false, cellAlign: 'left' }}>{children}<div className='last-spacer' key={'last'} /></SliderFlickity>} wrapperFalse={(children) => <ul>{children}</ul>} condition={isMobile && data.length > 1}>
        <>{Items}</>
      </ConditionalWrapper>
    </Section>
  )
}

const Section = styled.section`
  ${designGrid({})}
  margin-bottom: 75px;
  overflow-x: hidden;
  padding-top: 75px;
  width: 100vw;
  width: 100dvw;

  ${mq.greaterThan('tablet')} {
    margin-bottom: 110px;
    overflow-x: unset;
    padding-top: 110px;
  }

  h2 {
    ${presets.font40_60()}
    grid-column: 1 / span 6;
    margin-bottom: 50px;
    text-align: left;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 3;
      margin-bottom: 0;
    }

    ${mq.greaterThan('desktop-xs')} {
      grid-column: 1 / span 4;
    }
  }

  .slide {
    height: fit-content;
    margin-right: 20px;
    min-width: 300px;
  }

  > ul {
    align-items: flex-start;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 20px;
    grid-column: 1 / span 6;
    justify-content: space-between;
    max-width: 910px;

    ${mq.greaterThan('tablet')} {
      flex-wrap: wrap;
      gap: 10px;
      grid-column: 4 / span 9;
    }

    ${mq.greaterThan('desktop-xs')} {
      gap: 20px;
      grid-column: 5 / span 8;
    }

    ${mq.greaterThan('desktop')} {
      gap: 35px;
    }

    > .slide {
      height: fit-content;
      width: 100%;

      ${mq.greaterThan('mobile')} {
        width: calc(50% - 10px);
      }

      ${mq.greaterThan('tablet')} {
        margin-right: 0;
        min-width: unset;
        width: calc(50% - 5px);
      }

      ${mq.greaterThan('desktop-xs')} {
        width: calc(50% - 10px);
      }

      ${mq.greaterThan('desktop')} {
        width: calc(50% - 25px);
      }
    }
  }

  .flickity-slider {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
  }

  article {
    align-items: flex-start;
    border-radius: 20px;
    border: 1px solid ${rgbAlpha('--color-white', .2)};
    display: flex;
    flex-direction: column;
    height: fit-content;
    justify-content: space-between;
    min-height: unset;
    padding: 20px;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      display: flex;
      min-height: 500px;
      padding: 20px;
      max-width: 350px;
    }

    ${mq.greaterThan('desktop-xs')} {
      padding: 25px;
      max-width: 430px;
    }

    ${mq.greaterThan('desktop')} {
      padding: 40px 30px;
    }
  }

  h3 {
    ${presets.font30_40()}
  }

  .md {
    ${presets.font17_20_md()}
    padding: 30px 0;

    ${mq.greaterThan('tablet')} {
      padding: 50px 0;
    }

    ul {
      align-items: flex-start;
      display: flex;
      flex-direction: column;
      gap: 14.5px;
      justify-content: flex-start;

      ${mq.greaterThan('tablet')} {
        gap: 13px;
      }
    }

    li {
      align-items: center;
      display: flex;
      justify-content: flex-start;

      &:before {
        content: url('/images/svg/i--tick.svg');
        list-style: none;
        font-size: inherit;
        padding-right: 10px;
      }
    }

    p {
      margin-bottom: 10px;
      text-align: left;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .price-wrapper {
    align-items: baseline;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 20px;
    margin-bottom: auto;
    padding-bottom: 30px;

    ${mq.greaterThan('tablet')} {
      padding-bottom: 50px;
    }

    span {
      display: inline-block;
      width: auto;
      width: fit-content;

      &.discounted {
        ${presets.font40()}
      }

      &.full {
        ${presets.font20()}
        opacity: .4;
        text-decoration: line-through;
      }

      &.launch {
        color: var(--color-gold);
        ${presets.font15()}
        width: 100%;
      }
    }
  }

  button {
    align-items: center;
    align-self: stretch;
    background-color: var(--color-gold);
    border-radius: 100px;
    border: 2px solid var(--color-gold);
    color: var(--color-black);
    display: flex;
    ${presets.font20_pricing_cta()}
    justify-content: center;
    margin: 10px auto 0;
    padding: 20px;
    transition: 250ms background-color ease-in-out, 250ms color ease-in-out;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      margin: 0 auto;
    }

    &:hover {
      background-color: var(--color-black);
      color: var(--color-gold);
    }
  }
`
